import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CancelSubDialogComponent } from './cancel-sub-dialog/cancel-sub-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { BackendAccessService } from '../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../Services/Loading/loading-service.service';
import { SubProductsService } from '../../Services/sub-products.service';
import { AuthService } from '../../Services/Auth/auth.service';
import { WholesalerUpdateDialogComponent } from './wholesaler-update-dialog/wholesaler-update-dialog.component';

@Component({
  selector: 'app-active-subs',
  templateUrl: './active-subs.component.html',
  styleUrls: ['./active-subs.component.scss'],

  standalone: false,
})
/* istanbul ignore next */
export class ActiveSubsComponent {
  maxioSubId!: string;
  subscriptionDetail: any;
  displayedColumns: string[] = [
    'number',
    'issueDate',
    'dueDate',
    'status',
    'totalAmount',
    'dueAmount',
  ];
  subscriptionDetails: any;
  wholesalers: any[] = [];

  constructor(
    readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly backend: BackendAccessService,
    private readonly loading: LoadingService,
    private readonly router: Router,
    private readonly subProducts: SubProductsService,
    private readonly authService: AuthService
  ) {}
  /* istanbul ignore next */
  ngOnInit(): void {
    // Retrieve the MaxioSubId from query parameters
    this.route.queryParams.subscribe((params) => {
      this.maxioSubId = params['maxioSubId'];
      if (this.maxioSubId) {
        this.fetchSubscriptionDetails(this.maxioSubId);
      } else {
        console.error('No MaxioSubId provided in the query parameters.');
      }
    });
    this.wholesalers = this.authService.getWholesalers();
    console.log('Wholesalers from AuthService:', this.wholesalers);
  }
  /* istanbul ignore next */
  fetchSubscriptionDetails(maxioSubId: string): void {
    this.loading.show();
    this.backend.getIndividualSubDetails(maxioSubId).subscribe({
      next: (response) => {
        // Assign both subscriptionDetail and subscriptionMetaData
        this.subscriptionDetail = {
          ...response.subscriptionDetail,
          subscriptionMetaData: response.subscriptionMetaData,
          subscriptionComponents: response.subscriptionComponents,
          subscriptionInvoices: response.subscriptionInvoices,
          currentWholesalerDetails: response.currentWholesalerDetails,
          futureWholesalerDetails: response.futureWholesalerDetails,
        };
        console.log('Subscription Details:', this.subscriptionDetail);
        this.loading.hide();
      },
      error: (error) => {
        console.error('Error fetching subscription details:', error);
        this.loading.hide();
      },
    });
  }
  /* istanbul ignore next */
  getMetaFieldValue(fieldName: string): string {
    if (!this.subscriptionDetail?.subscriptionMetaData) {
      return 'N/A'; // Return default value if subscriptionMetaData is not available
    }

    const metadata = this.subscriptionDetail.subscriptionMetaData.find(
      (meta: { metaFieldName: string; metaFieldValue: string }) =>
        meta.metaFieldName === fieldName
    );

    return metadata?.metaFieldValue ?? 'Not Available'; // Safely return metaFieldValue or default value
  }
  /* istanbul ignore next */
  getFormattedPropertyAddress(): string {
    const address = this.getMetaFieldValue('property_address')?.toLowerCase();
    const city = this.getMetaFieldValue('property_city')?.toLowerCase();
    const state = this.getMetaFieldValue('property_state')?.toLowerCase();
    const zip = this.getMetaFieldValue('property_zip')?.toLowerCase();

    // If all are 'unknown', return 'Unknown' once
    if (
      address === 'unknown' &&
      city === 'unknown' &&
      state === 'unknown' &&
      zip === 'unknown'
    ) {
      return 'Unknown';
    }

    // Otherwise build address string excluding unknown or empty parts
    const parts = [];
    if (address && address !== 'unknown') parts.push(address);
    const cityStateZip = [city, state, zip]
      .filter((part) => part && part !== 'unknown')
      .join(' ');
    if (cityStateZip) parts.push(cityStateZip);

    return parts.length ? parts.join(', ') : 'N/A';
  }

  /* istanbul ignore next */
  openInvoice(url: string): void {
    if (url) {
      window.open(url, '_blank'); // Open the invoice in a new tab
    } else {
      console.error('Invoice URL is not available');
    }
  }
  /* istanbul ignore next */
  updateWholesaler(): void {
    const wholesalers = this.wholesalers || [];
    const currentEmail =
      this.subscriptionDetail?.currentWholesalerDetails?.userEmail ?? null;
    const futureEmail =
      this.subscriptionDetail?.futureWholesalerDetails?.userEmail ?? null;

    const ref = this.dialog.open(WholesalerUpdateDialogComponent, {
      width: '480px',
      data: {
        wholesalers,
        currentWholesalerEmail: currentEmail,
        futureWholesalerEmail: futureEmail,
      },
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.action !== 'update' || !result.wholesalerEmail) {
        return;
      }

      const selectedEmail = result.wholesalerEmail as string;
      const subscriptionId = this.subscriptionDetail?.subscription?.id;

      if (!subscriptionId) {
        console.error('Missing subscriptionId for update');
        return;
      }

      this.loading.show();
      this.backend.updateWholesaler(subscriptionId, selectedEmail).subscribe({
        next: (resp) => {
          this.loading.hide();
          if (resp?.isSuccess) {
            // refresh and notify user
            this.fetchSubscriptionDetails(this.maxioSubId);
          } else {
            console.error('Update failed:', resp?.errors);
          }
        },
        error: (err) => {
          this.loading.hide();
          console.error('Update error', err);
        },
      });
    });
  }

  /* istanbul ignore next */
  navigateToUpdatePaymentPage() {
    console.log('Subscription Detail:', this.subscriptionDetail);

    const customerId = this.subscriptionDetail?.subscription?.customer?.id;
    console.log('Customer ID:', customerId);

    if (customerId) {
      this.router.navigate(['/add-payment-method'], {
        queryParams: { customerId, maxioSubId: this.maxioSubId },
      });
    } else {
      console.error(
        'Customer ID is missing! Subscription Detail:',
        this.subscriptionDetail
      );
    }
  }
  /* istanbul ignore next */
  get isOneTimeProduct(): boolean {
    return this.subProducts.isOneTimeForSubscription(this.subscriptionDetail);
  }

  /* istanbul ignore next */
  getInvoiceStatus(status: number): string {
    switch (status) {
      case 0:
        return 'Draft';
      case 1:
        return 'Open';
      case 2:
        return 'Paid';
      case 3:
        return 'Pending';
      case 4:
        return 'Voided';
      case 5:
        return 'Cancelled';
      case 6:
        return 'Processing';
      default:
        return 'Unknown';
    }
  }
  /* istanbul ignore next */
  getStatusClass(status: number): string {
    switch (status) {
      case 0: // Draft
        return 'chip-draft';
      case 1: // Open
        return 'chip-open';
      case 2: // Paid
        return 'chip-paid';
      case 3: // Pending
        return 'chip-pending';
      case 4: // Voided
        return 'chip-voided';
      case 5: // Cancelled
        return 'chip-cancelled';
      case 6: // Processing
        return 'chip-processing';
      default:
        return 'chip-unknown'; // Unknown or other statuses
    }
  }

  /* istanbul ignore next */
  openCancelDialog(): void {
    this.dialog.open(CancelSubDialogComponent, {
      width: '400px', // Adjust the width as needed
    });
  }
}
